import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  User,
  UserProfile,
  Memoir,
  Chapter,
  ChapterVersion,
  Note,
  Todo,
  Share,
  Comment,
  LoginRequest,
  RegisterRequest,
  CreateMemoirRequest,
  UpdateMemoirRequest,
  CreateChapterRequest,
  UpdateChapterRequest,
  SaveChapterContentRequest,
  CreateNoteRequest,
  CreateTodoRequest,
  ChangePasswordRequest,
  UserStats,
  ChatRequest,
  ChatResponse,
  ChatContext,
  MemoirChatStats,
  AuthResponse,
  RegistrationResponse,
  EmailVerificationRequest,
  EmailVerificationResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyResetTokenRequest,
  VerifyResetTokenResponse,
  PaginatedResponse,
  APIError,
  ChatMessage,
  // Admin types
  UserSummary,
  UserDetailed,
  SubscriptionPlan,
  PlatformAnalytics,
  AdminAction,
  UsageLimits,
  SubscriptionAnalytics,
  UsageAnalytics,
  UserRoleChangeRequest,
  UserSubscriptionChangeRequest,
  SubscriptionPlanCreateRequest,
  SubscriptionPlanUpdateRequest,
  PaginatedUsersResponse,
  PaginatedAdminActionsResponse,
  AdminUsersFilters,
  AdminActionsFilters,
  PlanUsersResponse,
  UserRole,
  // AI Admin types
  AIProvider,
  AIConfiguration,
  AIConfigurationUpdate,
  PromptTemplate,
  PromptTemplateCreate,
  PromptTemplateUpdate,
  AIUsageAnalytics,
  ChapterAnalysisResponse,
  AIUsageStatus,
  FeatureFlag,
  FeatureFlagUpdate,
  AIUsageLog,
  PaginatedAIUsageLogsResponse,
  // AI Image Admin types
  AIImageProvider,
  AIImageConfiguration,
  AIImageConfigurationUpdate,
  ImagePromptTemplate,
  ImagePromptTemplateCreate,
  ImagePromptTemplateUpdate,
  PaginatedAIImageUsageLogsResponse
} from '../types/api';
import {
  ExportSettings,
  MemoirMetadata,
  MemoirMetadataUpdate,
  ExportFormat
} from '../types/export';

// Helper function to notify components that AI usage has been updated
const notifyAIUsageUpdated = () => {
  window.dispatchEvent(new Event('aiUsageUpdated'));
};

class APIClient {
  private client: AxiosInstance;
  private baseURL: string;
  private isRefreshing = false;
  private isRedirecting = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Debug logging
        console.log('🚀 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          data: config.data,
          headers: config.headers
        });

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data
        });
        return response;
      },
      async (error: AxiosError) => {
        console.error('❌ API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          data: error.response?.data,
          message: error.message
        });

        const originalRequest = error.config as any;

        if (error.response?.status === 401 && !originalRequest._retry) {
          console.log('🔄 401 detected for URL:', originalRequest.url);

          // Special case: if refresh endpoint itself returns 401, logout immediately
          if (originalRequest.url?.includes('/api/auth/refresh')) {
            console.log('❌ Refresh endpoint returned 401, session expired');
            this.handleSessionExpired();
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // If already refreshing, queue this request
          if (this.isRefreshing) {
            console.log('🔄 Already refreshing, queueing request');
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          const refreshToken = localStorage.getItem('refresh_token');

          if (!refreshToken) {
            console.log('❌ No refresh token available');
            this.handleSessionExpired();
            return Promise.reject(error);
          }

          this.isRefreshing = true;

          try {
            console.log('🔄 Attempting to refresh token...');
            // Use a separate axios instance to avoid interceptor loops
            const refreshClient = axios.create({
              baseURL: this.baseURL,
              timeout: 10000,
              headers: {
                'Content-Type': 'application/json',
              },
            });

            const response = await refreshClient.post('/api/auth/refresh', {}, {
              headers: {
                Authorization: `Bearer ${refreshToken}`
              }
            });

            console.log('✅ Token refresh successful');
            const { access_token } = response.data;
            localStorage.setItem('access_token', access_token);

            // Process failed queue
            this.processQueue(null, access_token);

            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return this.client(originalRequest);
          } catch (refreshError: any) {
            console.log('❌ Refresh token failed:', refreshError?.response?.data?.detail || refreshError.message);
            this.processQueue(refreshError, null);
            this.handleSessionExpired();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // For 429 errors, preserve the original response structure
        if (error.response?.status === 429) {
          return Promise.reject(error);
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  private handleSessionExpired() {
    // Prevent multiple redirects
    if (this.isRedirecting) {
      console.log('🚪 Already redirecting to login, ignoring');
      return;
    }

    this.isRedirecting = true;
    console.log('🚪 Session expired, clearing auth and redirecting to login');

    // Clear all auth data
    this.clearAuth();

    // Reject all queued requests
    this.processQueue(new Error('Session expired'), null);

    // Reset refresh state
    this.isRefreshing = false;

    // Only redirect if not already on login/signup pages
    const currentPath = window.location.pathname;
    if (!currentPath.includes('/login') && !currentPath.includes('/signup') && currentPath !== '/') {
      console.log('🔄 Redirecting to login from:', currentPath);
      // Use replace to prevent back button issues and clear history
      window.location.replace('/login');
    } else {
      console.log('🔄 Already on auth page, not redirecting');
      this.isRedirecting = false;
    }
  }

  private handleError(error: AxiosError): APIError {
    if (error.response) {
      const responseData = error.response.data as any;
      let detail = 'An error occurred';

      if (error.response.status === 422 && Array.isArray(responseData)) {
        // Handle FastAPI validation errors
        const validationErrors = responseData.map((err: any) => {
          const field = err.loc?.slice(1).join('.') || 'field';
          return `${field}: ${err.msg}`;
        });
        detail = validationErrors.join(', ');
      } else if (responseData?.detail) {
        if (typeof responseData.detail === 'string') {
          detail = responseData.detail;
        } else if (typeof responseData.detail === 'object' && responseData.detail.message) {
          // Preserve structured error objects that have a message field
          detail = responseData.detail.message;
        } else {
          detail = 'Validation error occurred';
        }
      } else if (responseData?.message) {
        detail = responseData.message;
      }

      return {
        detail,
        status_code: error.response.status,
      };
    } else if (error.request) {
      return {
        detail: 'Network error - please check your connection',
        status_code: 0,
      };
    } else {
      return {
        detail: error.message || 'Unknown error occurred',
        status_code: 0,
      };
    }
  }

  private clearAuth() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // Authentication
  async register(data: RegisterRequest): Promise<RegistrationResponse> {
    const response = await this.client.post('/api/auth/register', data);
    return response.data;
  }

  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post('/api/auth/login', data);
    const { access_token, refresh_token } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);

    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/api/auth/logout');
    } finally {
      this.clearAuth();
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.client.post('/api/auth/refresh', {}, {
      headers: {
        Authorization: `Bearer ${refreshToken}`
      }
    });

    const { access_token } = response.data;
    localStorage.setItem('access_token', access_token);

    return response.data;
  }

  // Email Verification
  async verifyEmail(data: EmailVerificationRequest): Promise<EmailVerificationResponse> {
    const response = await this.client.post('/api/auth/verify-email', data);
    return response.data;
  }

  async resendVerificationEmail(data: ResendVerificationRequest): Promise<ResendVerificationResponse> {
    const response = await this.client.post('/api/auth/resend-verification', data);
    return response.data;
  }

  // Password Reset
  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await this.client.post('/api/auth/forgot-password', data);
    return response.data;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await this.client.post('/api/auth/reset-password', data);
    return response.data;
  }

  async verifyResetToken(data: VerifyResetTokenRequest): Promise<VerifyResetTokenResponse> {
    const response = await this.client.post('/api/auth/verify-reset-token', data);
    return response.data;
  }

  // User Profile
  async getProfile(): Promise<User> {
    const response = await this.client.get('/api/users/profile');
    return response.data;
  }

  async getDetailedProfile(): Promise<UserProfile> {
    const response = await this.client.get('/api/users/profile');
    return response.data;
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.client.put('/api/users/profile', data);
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    const response = await this.client.post('/api/users/change-password', data);
    return response.data;
  }

  async getUserStats(): Promise<UserStats> {
    const response = await this.client.get('/api/users/stats');
    return response.data;
  }

  async deleteAccount(): Promise<{ message: string }> {
    const response = await this.client.delete('/api/users/account');
    return response.data;
  }

  // User Subscription Management
  async getActiveSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const response = await this.client.get('/api/users/subscription/plans');
    return response.data;
  }

  async upgradeSubscription(planName: string): Promise<{ message: string; new_plan: string; chapter_limit: number; ai_usage_limit: number }> {
    const response = await this.client.post('/api/users/subscription/upgrade', { plan_name: planName });
    return response.data;
  }

  // Stripe Payment Methods
  async createStripeCheckoutSession(
    planName: string,
    successUrl?: string,
    cancelUrl?: string
  ): Promise<{ session_id: string; client_secret: string; url: string }> {
    const response = await this.client.post('/api/stripe/create-checkout-session', {
      plan_name: planName,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data;
  }

  async createStripePortalSession(returnUrl?: string): Promise<{ url: string }> {
    const response = await this.client.post('/api/stripe/create-portal-session', {
      return_url: returnUrl,
    });
    return response.data;
  }

  async getSubscriptionStatus(): Promise<{
    subscription_tier: string;
    subscription_status: string;
    subscription_expires_at: string | null;
    has_stripe_subscription: boolean;
    stripe_customer_id: string | null;
    chapter_limit: number;
    ai_usage_limit: number;
    ai_usage_count: number;
  }> {
    const response = await this.client.get('/api/stripe/subscription-status');
    return response.data;
  }

  // Memoirs
  async getMemoirs(page = 1, size = 20): Promise<PaginatedResponse<Memoir>> {
    const response = await this.client.get('/api/memoirs', {
      params: { page, size },
    });
    return response.data;
  }

  async getMemoir(memoirId: string): Promise<Memoir> {
    const response = await this.client.get(`/api/memoirs/${memoirId}`);
    return response.data;
  }

  async createMemoir(data: CreateMemoirRequest): Promise<Memoir> {
    const response = await this.client.post('/api/memoirs', data);
    return response.data;
  }

  async updateMemoir(memoirId: string, data: UpdateMemoirRequest): Promise<Memoir> {
    const response = await this.client.put(`/api/memoirs/${memoirId}`, data);
    return response.data;
  }

  async deleteMemoir(memoirId: string): Promise<void> {
    await this.client.delete(`/api/memoirs/${memoirId}`);
  }

  async updateLastViewedChapter(memoirId: string, chapterId: string): Promise<void> {
    await this.client.put(`/api/memoirs/${memoirId}/last-viewed-chapter/${chapterId}`);
  }

  async getLastViewedChapter(memoirId: string): Promise<{memoir_id: string, last_viewed_chapter_id: string | null}> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/last-viewed-chapter`);
    return response.data;
  }

  // Chapters
  async getChapters(memoirId: string): Promise<Chapter[]> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/chapters`);
    return response.data;
  }

  async getChapter(chapterId: string): Promise<Chapter> {
    const response = await this.client.get(`/api/chapters/${chapterId}`);
    return response.data;
  }

  async createChapter(memoirId: string, data: CreateChapterRequest): Promise<Chapter> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/chapters`, data);
    return response.data;
  }

  async updateChapter(chapterId: string, data: UpdateChapterRequest): Promise<Chapter> {
    const response = await this.client.put(`/api/chapters/${chapterId}`, data);
    return response.data;
  }

  async deleteChapter(chapterId: string): Promise<void> {
    await this.client.delete(`/api/chapters/${chapterId}`);
  }

  async reorderChapters(memoirId: string, chapterIds: string[]): Promise<Chapter[]> {
    const response = await this.client.put(`/api/memoirs/${memoirId}/chapters/reorder`, {
      chapter_ids: chapterIds,
    });
    return response.data;
  }

  // Chapter Versions & Content
  async getChapterVersions(chapterId: string): Promise<ChapterVersion[]> {
    const response = await this.client.get(`/api/chapters/${chapterId}/versions`);
    return response.data;
  }

  async getChapterVersion(versionId: string): Promise<ChapterVersion> {
    const response = await this.client.get(`/api/versions/${versionId}`);
    return response.data;
  }

  async getChapterContent(chapterId: string): Promise<{ content: any; word_count: number; version_number: number; version_id?: string }> {
    const response = await this.client.get(`/api/chapters/${chapterId}/content`);
    return response.data;
  }

  async saveChapterContent(chapterId: string, data: SaveChapterContentRequest): Promise<ChapterVersion> {
    const response = await this.client.post(`/api/chapters/${chapterId}/save`, data);
    return response.data;
  }

  async restoreVersion(versionId: string): Promise<ChapterVersion> {
    const response = await this.client.post(`/api/versions/${versionId}/restore`);
    return response.data;
  }

  // Notes
  async getNotes(chapterId: string): Promise<Note[]> {
    const response = await this.client.get(`/api/chapters/${chapterId}/notes`);
    return response.data;
  }

  async createNote(chapterId: string, data: CreateNoteRequest): Promise<Note> {
    const response = await this.client.post(`/api/chapters/${chapterId}/notes`, data);
    return response.data;
  }

  async updateNote(noteId: string, data: Partial<CreateNoteRequest>): Promise<Note> {
    // Use the actual backend endpoint: /api/chapters/notes/{noteId}
    const response = await this.client.put(`/api/chapters/notes/${noteId}`, data);
    return response.data;
  }

  async deleteNote(noteId: string): Promise<void> {
    // Use the actual backend endpoint: /api/chapters/notes/{noteId}
    await this.client.delete(`/api/chapters/notes/${noteId}`);
  }

  // Todos (memoir-level)
  async getTodos(memoirId: string): Promise<Todo[]> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/todos`);
    return response.data;
  }

  async createTodo(memoirId: string, data: CreateTodoRequest): Promise<Todo> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/todos`, data);
    return response.data;
  }

  async updateTodo(todoId: string, data: Partial<CreateTodoRequest & { is_completed: boolean }>): Promise<Todo> {
    const response = await this.client.put(`/api/memoirs/todos/${todoId}`, data);
    return response.data;
  }

  async deleteTodo(todoId: string): Promise<void> {
    await this.client.delete(`/api/memoirs/todos/${todoId}`);
  }



  async chatWithMemoir(data: ChatRequest): Promise<ChatResponse> {
    const response = await this.client.post('/api/ai/chat', data);
    notifyAIUsageUpdated();
    return response.data;
  }

  async getMemoirChatStats(memoirId: string): Promise<MemoirChatStats> {
    const response = await this.client.get(`/api/ai/chat/stats/${memoirId}`);
    return response.data;
  }

  async generateWritingPrompts(memoirId: string, context?: string): Promise<string[]> {
    const response = await this.client.post('/api/ai/prompts', {
      memoir_id: memoirId,
      context,
    });
    return response.data.prompts;
  }

  // Export
  async exportMemoir(memoirId: string, format: 'txt' | 'markdown' | 'html' | 'pdf' | 'docx'): Promise<{ download_url: string }> {
    const response = await this.client.get(`/api/export/memoir/${memoirId}`, {
      params: { format },
    });
    return response.data;
  }

  async exportChapter(chapterId: string, format: 'txt' | 'markdown' | 'html' | 'pdf' | 'docx'): Promise<{ download_url: string }> {
    const response = await this.client.get(`/api/export/chapter/${chapterId}`, {
      params: { format },
    });
    return response.data;
  }

  // Collaboration
  async createShare(memoirId: string, email: string, permission: 'read' | 'comment' | 'edit'): Promise<Share> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/shares`, {
      shared_with_email: email,
      permission_level: permission,
    });
    return response.data;
  }

  async getShares(memoirId: string): Promise<Share[]> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/shares`);
    return response.data;
  }

  async deleteShare(shareId: string): Promise<void> {
    await this.client.delete(`/api/shares/${shareId}`);
  }

  // Comments
  async getComments(chapterVersionId: string): Promise<Comment[]> {
    const response = await this.client.get(`/api/versions/${chapterVersionId}/comments`);
    return response.data;
  }

  async createComment(chapterVersionId: string, content: string, position?: Record<string, any>): Promise<Comment> {
    const response = await this.client.post(`/api/versions/${chapterVersionId}/comments`, {
      content,
      position,
    });
    return response.data;
  }

  async updateComment(commentId: string, content: string): Promise<Comment> {
    const response = await this.client.put(`/api/comments/${commentId}`, {
      content,
    });
    return response.data;
  }

  async deleteComment(commentId: string): Promise<void> {
    await this.client.delete(`/api/comments/${commentId}`);
  }

  // Utility method to check if user is authenticated
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  // Admin API Methods

  // User Management
  async getAdminUsers(filters: AdminUsersFilters = {}): Promise<PaginatedUsersResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.size) params.append('size', filters.size.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.role_filter) params.append('role_filter', filters.role_filter);
    if (filters.status_filter) params.append('status_filter', filters.status_filter);
    if (filters.subscription_filter) params.append('subscription_filter', filters.subscription_filter);

    const response = await this.client.get(`/api/admin/users?${params.toString()}`);
    return response.data;
  }

  async getAdminUserDetails(userId: string): Promise<UserDetailed> {
    const response = await this.client.get(`/api/admin/users/${userId}`);
    return response.data;
  }

  async activateUser(userId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/admin/users/${userId}/activate`);
    return response.data;
  }

  async deactivateUser(userId: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/admin/users/${userId}/deactivate`);
    return response.data;
  }

  async changeUserRole(userId: string, data: UserRoleChangeRequest): Promise<{ message: string }> {
    const response = await this.client.put(`/api/admin/users/${userId}/role`, data);
    return response.data;
  }

  async changeUserSubscription(userId: string, data: UserSubscriptionChangeRequest): Promise<{ message: string }> {
    const response = await this.client.put(`/api/admin/users/${userId}/subscription`, data);
    return response.data;
  }

  // Analytics
  async getPlatformAnalytics(): Promise<PlatformAnalytics> {
    const response = await this.client.get('/api/admin/analytics/overview');
    return response.data;
  }

  async getSubscriptionAnalytics(): Promise<SubscriptionAnalytics> {
    const response = await this.client.get('/api/admin/analytics/subscriptions');
    return response.data;
  }

  async getUsageAnalytics(): Promise<UsageAnalytics> {
    const response = await this.client.get('/api/admin/analytics/usage');
    return response.data;
  }

  // Subscription Plans
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const response = await this.client.get('/api/admin/plans');
    return response.data;
  }

  async createSubscriptionPlan(data: SubscriptionPlanCreateRequest): Promise<SubscriptionPlan> {
    const response = await this.client.post('/api/admin/plans', data);
    return response.data;
  }

  async updateSubscriptionPlan(planId: string, data: SubscriptionPlanUpdateRequest): Promise<SubscriptionPlan> {
    const response = await this.client.put(`/api/admin/plans/${planId}`, data);
    return response.data;
  }

  async deleteSubscriptionPlan(planId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/admin/plans/${planId}`);
    return response.data;
  }

  async getPlanUsers(planName: string): Promise<PlanUsersResponse> {
    const response = await this.client.get(`/api/admin/plans/${planName}/users`);
    return response.data;
  }

  // Admin Actions History
  async getAdminActions(filters: AdminActionsFilters = {}): Promise<PaginatedAdminActionsResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.size) params.append('size', filters.size.toString());
    if (filters.admin_id) params.append('admin_id', filters.admin_id);
    if (filters.action_type) params.append('action_type', filters.action_type);

    const response = await this.client.get(`/api/admin/actions?${params.toString()}`);
    return response.data;
  }

  // Usage Limits
  async getUserUsageLimits(userId: string): Promise<UsageLimits> {
    const response = await this.client.get(`/api/admin/users/${userId}/limits`);
    return response.data;
  }

  // Check if current user is admin based on profile (secure - no API endpoint exposure)
  async isAdmin(): Promise<boolean> {
    try {
      const profile = await this.getDetailedProfile();
      return profile.user_role === 'admin' || profile.user_role === 'super_admin';
    } catch (error) {
      return false;
    }
  }

  // Legacy method for admin pages that need server-side verification
  // Only use this in admin pages where 403 errors are expected for non-admins
  async verifyAdminAccess(): Promise<boolean> {
    try {
      await this.client.get('/api/admin/analytics/overview');
      return true;
    } catch (error) {
      return false;
    }
  }

  // AI Admin API Methods

  // AI Configuration Management
  async getAIConfigurations(): Promise<AIConfiguration[]> {
    const response = await this.client.get('/api/admin/ai/configuration');
    return response.data;
  }

  async getAIProviders(): Promise<AIProvider[]> {
    const response = await this.client.get('/api/admin/ai/providers');
    return response.data;
  }

  async updateAIConfiguration(providerName: string, data: AIConfigurationUpdate): Promise<AIConfiguration> {
    const response = await this.client.put(`/api/admin/ai/configuration/${providerName}`, data);
    return response.data;
  }

  // Prompt Template Management
  async getPromptTemplates(providerName?: string): Promise<PromptTemplate[]> {
    const params = new URLSearchParams();
    if (providerName) params.append('provider_name', providerName);

    const response = await this.client.get(`/api/admin/ai/prompts?${params.toString()}`);
    return response.data;
  }

  async createPromptTemplate(data: PromptTemplateCreate): Promise<PromptTemplate> {
    const response = await this.client.post('/api/admin/ai/prompts', data);
    return response.data;
  }

  async updatePromptTemplate(templateId: string, data: PromptTemplateUpdate): Promise<PromptTemplate> {
    const response = await this.client.put(`/api/admin/ai/prompts/${templateId}`, data);
    return response.data;
  }

  async deletePromptTemplate(templateId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/admin/ai/prompts/${templateId}`);
    return response.data;
  }

  // AI Usage Analytics
  async getAIUsageAnalytics(days: number = 30): Promise<AIUsageAnalytics> {
    const response = await this.client.get(`/api/admin/ai/usage-analytics?days=${days}`);
    return response.data;
  }

  async getAIUsageLogs(filters: {
    page?: number;
    size?: number;
    user_id?: string;
    status?: string;
  } = {}): Promise<PaginatedAIUsageLogsResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.size) params.append('size', filters.size.toString());
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.status) params.append('status', filters.status);

    const response = await this.client.get(`/api/admin/ai/usage-logs?${params.toString()}`);
    return response.data;
  }

  // Feature Flag Management
  async getFeatureFlags(): Promise<FeatureFlag[]> {
    const response = await this.client.get('/api/admin/ai/feature-flags');
    return response.data;
  }

  async updateFeatureFlag(flagName: string, data: FeatureFlagUpdate): Promise<FeatureFlag> {
    const response = await this.client.put(`/api/admin/ai/feature-flags/${flagName}`, data);
    return response.data;
  }

  // AI Image Configuration Management
  async getImageProviders(): Promise<AIImageProvider[]> {
    const response = await this.client.get('/api/admin/ai/image-providers');
    return response.data;
  }

  async getImageConfigurations(): Promise<AIImageConfiguration[]> {
    const response = await this.client.get('/api/admin/ai/image-configuration');
    return response.data;
  }

  async updateImageConfiguration(providerName: string, data: AIImageConfigurationUpdate): Promise<AIImageConfiguration> {
    const response = await this.client.put(`/api/admin/ai/image-configuration/${providerName}`, data);
    return response.data;
  }

  async getImagePromptTemplates(providerName?: string): Promise<ImagePromptTemplate[]> {
    const params = new URLSearchParams();
    if (providerName) params.append('provider_name', providerName);

    const response = await this.client.get(`/api/admin/ai/image-prompts?${params.toString()}`);
    return response.data;
  }

  async createImagePromptTemplate(data: ImagePromptTemplateCreate): Promise<ImagePromptTemplate> {
    const response = await this.client.post('/api/admin/ai/image-prompts', data);
    return response.data;
  }

  async updateImagePromptTemplate(templateId: string, data: ImagePromptTemplateUpdate): Promise<ImagePromptTemplate> {
    const response = await this.client.put(`/api/admin/ai/image-prompts/${templateId}`, data);
    return response.data;
  }

  async deleteImagePromptTemplate(templateId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/admin/ai/image-prompts/${templateId}`);
    return response.data;
  }

  async getImageUsageLogs(filters: {
    page?: number;
    size?: number;
    user_id?: string;
    status?: string;
  } = {}): Promise<PaginatedAIImageUsageLogsResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.size) params.append('size', filters.size.toString());
    if (filters.user_id) params.append('user_id', filters.user_id);
    if (filters.status) params.append('status', filters.status);

    const response = await this.client.get(`/api/admin/ai/image-usage-logs?${params.toString()}`);
    return response.data;
  }

  // User AI Methods (for chapter analysis)
  async analyzeChapter(chapterId: string): Promise<ChapterAnalysisResponse> {
    const response = await this.client.post(`/api/ai/analyze/${chapterId}`);
    notifyAIUsageUpdated();
    return response.data;
  }

  async getAIUsageStatus(): Promise<AIUsageStatus> {
    const response = await this.client.get('/api/ai/usage-status');
    return response.data;
  }

  // Image Upload
  async uploadImage(formData: FormData): Promise<{
    file_path: string;
    download_url: string;
    filename: string;
    size: number;
    content_type: string;
  }> {
    const response = await this.client.post('/api/images/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // AI Image Generation
  async generateImagePrompt(chapterId: string): Promise<{
    image_prompt: string;
    usage_info: any;
    metadata: any;
  }> {
    const response = await this.client.post('/api/ai/generate-image-prompt', {
      chapter_id: chapterId,
    });
    notifyAIUsageUpdated();
    return response.data;
  }

  async generateImage(imagePrompt: string, chapterId?: string): Promise<{
    image_url: string;
    file_path: string;
    prompt_used: string;
    size: number;
    usage_info: any;
    metadata: any;
  }> {
    const response = await this.client.post('/api/ai/generate-image', {
      image_prompt: imagePrompt,
      chapter_id: chapterId,
    });
    notifyAIUsageUpdated();
    return response.data;
  }

  // AI Paragraph Improvement
  async improveParagraph(chapterId: string, paragraphText: string): Promise<{
    improved_text: string;
    original_text: string;
    usage_info: {
      tokens_used: number;
      estimated_cost: number;
      response_time_ms: number;
      remaining_requests: number;
    };
    metadata: {
      model_used: string;
      template_version: number;
      analysis_type: string;
      word_count: number;
    };
  }> {
    const response = await this.client.post('/api/ai/improve-paragraph', {
      chapter_id: chapterId,
      paragraph_text: paragraphText,
    });
    notifyAIUsageUpdated();
    return response.data;
  }

  // Book Export Methods

  /**
   * Export memoir to EPUB format with default settings
   */
  async exportMemoirEPUB(memoirId: string): Promise<Blob> {
    const response = await this.client.get(`/api/export/memoirs/${memoirId}/export/epub`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export memoir to EPUB format with custom settings
   */
  async exportMemoirEPUBWithSettings(memoirId: string, settings: ExportSettings): Promise<Blob> {
    const response = await this.client.post(`/api/export/memoirs/${memoirId}/export/epub`, settings, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export memoir to PDF format
   */
  async exportMemoirPDF(memoirId: string): Promise<Blob> {
    const response = await this.client.get(`/api/export/memoirs/${memoirId}/export/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Export memoir to PDF format with custom settings
   */
  async exportMemoirPDFWithSettings(memoirId: string, settings: ExportSettings): Promise<Blob> {
    const response = await this.client.post(`/api/export/memoirs/${memoirId}/export/pdf`, settings, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Get memoir publishing metadata
   */
  async getMemoirMetadata(memoirId: string): Promise<MemoirMetadata> {
    const response = await this.client.get(`/api/export/memoirs/${memoirId}/metadata`);
    return response.data;
  }

  /**
   * Update memoir publishing metadata
   */
  async updateMemoirMetadata(memoirId: string, metadata: MemoirMetadataUpdate): Promise<{
    success: boolean;
    message: string;
    memoir_id: string;
  }> {
    const response = await this.client.put(`/api/export/memoirs/${memoirId}/metadata`, metadata);
    return response.data;
  }

  /**
   * Get publishing metadata for covers
   */
  async getPublishingMetadata(memoirId: string): Promise<any> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/publishing`);
    return response.data;
  }

  /**
   * Update publishing metadata for covers
   */
  async updatePublishingMetadata(memoirId: string, metadata: any): Promise<any> {
    const response = await this.client.patch(`/api/memoirs/${memoirId}/publishing`, metadata);
    return response.data;
  }

  /**
   * Generate AI cover prompt based on memoir content
   */
  async generateCoverPrompt(memoirId: string, coverType: 'front' | 'back'): Promise<{
    cover_type: string;
    generated_prompt: string;
    usage_info: any;
    usage_remaining: number;
  }> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/generate-cover-prompt`, {
      cover_type: coverType
    });
    notifyAIUsageUpdated();
    return response.data;
  }

  /**
   * Generate AI cover image
   */
  async generateCoverImage(memoirId: string, coverType: 'front' | 'back', regenerate: boolean = false, customPrompt?: string): Promise<{
    cover_type: string;
    image_url: string;
    prompt_used: string;
    generation_cost: number;
    usage_remaining: number;
    usage_info?: {
      credits_used: number;
      credits_remaining: number;
      estimated_cost: number;
      response_time_ms: number;
    };
  }> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/generate-cover`, {
      cover_type: coverType,
      regenerate,
      custom_prompt: customPrompt || null
    });
    notifyAIUsageUpdated();
    return response.data;
  }

  async uploadCoverImage(memoirId: string, coverType: 'front' | 'back', formData: FormData): Promise<{
    cover_type: string;
    image_url: string;
    file_size: number;
    original_size: number;
    optimization_ratio: number;
    format: string;
  }> {
    const response = await this.client.post(
      `/api/memoirs/${memoirId}/upload-cover?cover_type=${coverType}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // Async Export Methods

  /**
   * Start async PDF export in background
   */
  async startPDFExport(memoirId: string): Promise<{
    status: string;
    memoir_id: string;
    message: string;
  }> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/export/pdf/start`);
    return response.data;
  }

  /**
   * Start async EPUB export in background
   */
  async startEPUBExport(memoirId: string): Promise<{
    status: string;
    memoir_id: string;
    message: string;
  }> {
    const response = await this.client.post(`/api/memoirs/${memoirId}/export/epub/start`);
    return response.data;
  }

  /**
   * Get export status for both PDF and EPUB
   */
  async getExportStatus(memoirId: string): Promise<{
    pdf: {
      status: 'not_started' | 'in_progress' | 'completed' | 'failed';
      generated_at: string | null;
      filename: string | null;
    };
    epub: {
      status: 'not_started' | 'in_progress' | 'completed' | 'failed';
      generated_at: string | null;
      filename: string | null;
    };
  }> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/export/status`);
    return response.data;
  }

  /**
   * Download completed PDF export
   */
  async downloadPDFExport(memoirId: string): Promise<Blob> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/export/pdf/download`, {
      responseType: 'blob',
      timeout: 300000, // 5 minutes for large PDFs with many images
    });
    return response.data;
  }

  /**
   * Download completed EPUB export
   */
  async downloadEPUBExport(memoirId: string): Promise<Blob> {
    const response = await this.client.get(`/api/memoirs/${memoirId}/export/epub/download`, {
      responseType: 'blob',
      timeout: 300000, // 5 minutes for large EPUBs with many images
    });
    return response.data;
  }
}

export const apiClient = new APIClient();
export default apiClient;
